import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Node, mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import ImageResize from 'tiptap-extension-resize-image';

/**
 * Extends ImageResize so alignment/resize serializes as a real inline `style`
 * attribute on the <img>, instead of the extension's default
 * `containerstyle="..."` custom attribute (which DOMPurify / backend
 * sanitizers strip, leaving Preview/Help Center unaligned).
 *
 * On save: extract usable declarations from `containerStyle` (drop editor-only
 * props like `cursor`/`height: auto`/dashed border) and emit them as `<img style="...">`.
 * On load: parseHTML prefers the inline `style` attribute so the round-trip
 * preserves margin/alignment even without the legacy `containerstyle` attribute.
 */
const ResizableAlignableImage = ImageResize.extend({
    // Flip the base extension's `allowBase64` default so <img src="data:..."> survives
    // the parseHTML rule on setContent (default is false → schema silently drops them).
    addOptions() {
        const parent = (this as any).parent?.() ?? {};
        return { ...parent, allowBase64: true };
    },
    // Override parseHTML so the `data:`-URL exclusion is lifted regardless of
    // whether the ImageResize type declaration exposes `allowBase64`.
    parseHTML() {
        return [{ tag: 'img[src]' }];
    },
    addAttributes() {
        const parent = (this as any).parent?.() ?? {};
        return {
            ...parent,
            containerStyle: {
                ...parent.containerStyle,
                parseHTML: (element: HTMLElement) => {
                    // Prefer inline style (preserves our serialized margin+width)
                    const inline = element.getAttribute('style');
                    if (inline) return inline;
                    // Legacy content: custom containerstyle attr
                    const legacy = element.getAttribute('containerstyle');
                    if (legacy) return legacy;
                    // Oldest fallback: just the width attribute
                    const width = element.getAttribute('width');
                    return width
                        ? `width: ${width}px; height: auto; cursor: pointer;`
                        : '';
                },
            },
        };
    },
    renderHTML({ HTMLAttributes }: { HTMLAttributes: Record<string, any> }) {
        const attrs: Record<string, any> = { ...HTMLAttributes };
        // Drop the extension's custom attrs — we transform them into `style`
        const containerStyle = attrs.containerstyle || attrs.containerStyle;
        delete attrs.containerstyle;
        delete attrs.containerStyle;
        delete attrs.wrapperstyle;
        delete attrs.wrapperStyle;

        if (typeof containerStyle === 'string' && containerStyle.trim()) {
            const EDITOR_ONLY = new Set(['cursor', 'border', 'position']);
            const declarations = containerStyle
                .split(';')
                .map((s) => s.trim())
                .filter(Boolean);
            const kept: string[] = [];
            let hasAutoMargin = false;
            let hasDisplay = false;
            for (const decl of declarations) {
                const idx = decl.indexOf(':');
                if (idx < 0) continue;
                const prop = decl.slice(0, idx).trim().toLowerCase();
                const value = decl.slice(idx + 1).trim();
                if (!prop || !value) continue;
                if (EDITOR_ONLY.has(prop)) continue;
                // Drop `height: auto` — it's editor cruft; real height comes from width + aspect ratio
                if (prop === 'height' && value === 'auto') continue;
                kept.push(`${prop}: ${value}`);
                if (prop === 'margin' && /\bauto\b/.test(value)) hasAutoMargin = true;
                if (prop === 'display') hasDisplay = true;
            }
            // `margin: * auto *` only centers/aligns a block-level element
            if (hasAutoMargin && !hasDisplay) kept.push('display: block');
            if (kept.length) attrs.style = kept.join('; ') + ';';
        }
        return ['img', mergeAttributes((this as any).options.HTMLAttributes ?? {}, attrs)];
    },
});
import Placeholder from '@tiptap/extension-placeholder';

// Contract-KnowledgeTablePaste.md §3.
//
// 🚨 TipTap v3 ships Table/TableRow/TableHeader/TableCell from this ONE
// package; v2 split them across four (`@tiptap/extension-table-row`, …).
// Installing the v2 set against a v3 core typechecks and then misbehaves.
// Verified against the installed package's export map before writing this
// import. `TableKit` is the v3 bundle that registers all four at once — using
// it (rather than four separate entries) is what guarantees §2's identical
// node registration order across both editors.
import { TableKit } from '@tiptap/extension-table';
// `@tiptap/pm` is TipTap's own ProseMirror bundle and a declared peer of
// @tiptap/core, so it is always present. Deliberately NOT added to
// package.json: pinning it explicitly could resolve a second copy of
// prosemirror-model (the two repos' cores are on different 3.x patches), and
// two ProseMirror instances break every `instanceof` in the schema.
import { Fragment, Slice } from '@tiptap/pm/model';

// Editor-side mirror of the published Help Center's table CSS
// (`back/templates/help_center/article.html:29-31`) so WYSIWYG actually holds.
// `block` + `overflow-x-auto` is the mobile story in both places: a wide table
// scrolls inside itself instead of widening the editor.
//
// Borders/padding/weight match the HC exactly. Colours cannot: the HC page is
// light-only, this editor has a dark mode, so dark variants are the nearest
// equivalents rather than the same hex.
//
// 🚨 Must stay byte-identical to the same constant in
// `admin-front/src/components/knowledge/RichTextEditor.tsx`.
const TABLE_PROSE_CLASSES = [
    '[&_table]:w-full [&_table]:border-collapse [&_table]:my-4 [&_table]:block [&_table]:overflow-x-auto',
    '[&_th]:border [&_th]:border-gray-200 dark:[&_th]:border-gray-700',
    '[&_th]:bg-gray-50 dark:[&_th]:bg-gray-800 [&_th]:px-3 [&_th]:py-2',
    '[&_th]:text-left [&_th]:font-semibold',
    '[&_td]:border [&_td]:border-gray-200 dark:[&_td]:border-gray-700',
    '[&_td]:px-3 [&_td]:py-2',
    // prosemirror-tables marks the active cell; without this the selection is
    // invisible inside a table.
    '[&_.selectedCell]:bg-primary/10',
].join(' ');

// Owner decision 2026-08-06 (addendum to Contract-KnowledgeTablePaste.md §3.2):
// a pasted table's FIRST ROW always becomes a header row.
//
// Why this is needed: Google Sheets, Google Docs and Excel all emit `<td>` for
// their header row — the heading is just bold text, never `<th>`. TipTap
// preserves that faithfully, so without this a pasted spreadsheet reaches the
// Help Center with no `.prose th` shading at all.
//
// Runs in `transformPasted`, so it sees ONLY the incoming slice: tables already
// in the document are never touched, and the author's later edits are never
// re-promoted.
//
// 🚨 Must stay byte-identical to the same function in
// `admin-front/src/components/knowledge/RichTextEditor.tsx`.
function promoteFirstRowToHeader(slice: Slice, schema: any): Slice {
    const headerType = schema.nodes.tableHeader;
    const tableType = schema.nodes.table;
    // Compact mode (the Inbox email composer) never registers TableKit, so
    // these node types don't exist there and this is a no-op.
    if (!headerType || !tableType) return slice;

    const promoteTable = (table: any) => {
        const firstRow = table.firstChild;
        if (!firstRow || firstRow.childCount === 0) return table;

        // Single-row tables are left alone (owner decision 2026-08-06).
        // Someone pasting one row is almost certainly pasting DATA, not a
        // heading, and promoting it yields an all-header table with no body —
        // which reads as a table whose data went missing. They can still reach
        // for "Toggle header row" if they did mean it as a header.
        if (table.childCount === 1) return table;

        // Idempotent: a source that already ships <th> comes out untouched.
        // Matches prosemirror-tables' own `isHeaderEnabledByType('row')`, which
        // treats a row as a header row only when EVERY cell is a header cell —
        // so a partially-header row is completed rather than left mixed.
        let allHeader = true;
        firstRow.forEach((cell: any) => {
            if (cell.type !== headerType) allHeader = false;
        });
        if (allHeader) return table;

        const cells: any[] = [];
        firstRow.forEach((cell: any) => {
            // The same swap prosemirror-tables' toggleHeader('row') performs:
            // change the node type but carry the ORIGINAL attrs across, so
            // colspan / rowspan / colwidth survive exactly as the built-in
            // command would leave them.
            cells.push(headerType.create(cell.attrs, cell.content, cell.marks));
        });

        const rows: any[] = [firstRow.copy(Fragment.fromArray(cells))];
        for (let i = 1; i < table.childCount; i += 1) rows.push(table.child(i));
        return table.copy(Fragment.fromArray(rows));
    };

    // Tables can be nested inside the pasted fragment (a table inside a list
    // item, say), so walk rather than only checking top-level nodes.
    const walk = (fragment: Fragment): Fragment => {
        const out: any[] = [];
        let changed = false;
        fragment.forEach((node: any) => {
            let next = node;
            if (node.type === tableType) {
                next = promoteTable(node);
            } else if (node.childCount > 0) {
                const inner = walk(node.content);
                if (inner !== node.content) next = node.copy(inner);
            }
            if (next !== node) changed = true;
            out.push(next);
        });
        return changed ? Fragment.fromArray(out) : fragment;
    };

    const content = walk(slice.content);
    if (content === slice.content) return slice;
    // Depth is unchanged by a node-type swap, so the open depths carry over.
    return new Slice(content, slice.openStart, slice.openEnd);
}
import {
    Bold, Italic, Link as LinkIcon, Image as ImageIcon, List, ListOrdered, Quote,
    X, Heading1, Heading2, Heading3, Video as VideoIcon, RemoveFormatting, Loader2,
    Sparkles, AlertTriangle,
    Table as TableIcon, ChevronDown, ArrowUp, ArrowDown, ArrowLeft, ArrowRight,
    PanelTop, Trash2,
} from 'lucide-react';
import ConfirmModal from './ConfirmModal';
import { knowledgeAPI, knowledgeAIAPI } from '../services/api';
import { useBilling } from '../contexts/BillingContext';
import { useToast } from '../contexts/ToastContext';
import type { Plan } from '../types/billing';
import { isPooledBilling } from '../types/billing';
import {
    VIDEO_ANALYSIS_PLAN_CAP_MB,
    VIDEO_ANALYSIS_DEFAULT_QUOTA,
    isVideoFile,
    isYouTubeUrl,
    youtubeEmbedHtml,
    uploadedVideoEmbedHtml,
    buildAnalyzedArticleHtml,
    formatAnalyzeError,
} from '../utils/videoAnalysis';

export type RichTextEditorMode = 'email' | 'article';

interface RichTextEditorProps {
    content: string;
    onChange: (html: string, text: string) => void;
    placeholder?: string;
    mode?: RichTextEditorMode;
    /** Required for article mode to enable S3 image/video uploads. */
    agentId?: string;
    /** Called with the extracted S3 key every time a media file is uploaded from this editor. */
    onMediaUploaded?: (s3Key: string) => void;
}

/** Extract the S3 object key from a full S3 URL. Returns null if not an S3 URL. */
function extractS3Key(url: string): string | null {
    try {
        const u = new URL(url);
        // Match both virtual-hosted and path-style S3 URLs
        if (!u.hostname.includes('.s3.') && !u.hostname.startsWith('s3.')) return null;
        return decodeURIComponent(u.pathname.replace(/^\//, ''));
    } catch {
        return null;
    }
}

// --- Custom TipTap nodes so raw <video> / <iframe> round-trip through the editor ---
const VideoBlock = Node.create({
    name: 'video',
    group: 'block',
    atom: true,
    draggable: true,
    selectable: true,
    addAttributes() {
        return {
            src: {
                default: null,
                parseHTML: (el: HTMLElement) => el.getAttribute('src'),
                renderHTML: (attrs: any) => (attrs.src ? { src: attrs.src } : {}),
            },
            controls: {
                default: 'controls',
                parseHTML: () => 'controls',
                renderHTML: () => ({ controls: 'controls' }),
            },
            preload: {
                default: 'metadata',
                parseHTML: (el: HTMLElement) => el.getAttribute('preload') || 'metadata',
                renderHTML: (attrs: any) => ({ preload: attrs.preload || 'metadata' }),
            },
        };
    },
    parseHTML() {
        return [{ tag: 'video' }];
    },
    renderHTML({ HTMLAttributes }) {
        return [
            'video',
            mergeAttributes(HTMLAttributes, { class: 'max-w-full rounded-lg my-2' }),
        ];
    },
});

const IframeBlock = Node.create({
    name: 'iframe',
    group: 'block',
    atom: true,
    draggable: true,
    selectable: true,
    addAttributes() {
        return {
            src: {
                default: null,
                parseHTML: (el: HTMLElement) => el.getAttribute('src'),
                renderHTML: (attrs: any) => (attrs.src ? { src: attrs.src } : {}),
            },
            frameborder: {
                default: '0',
                parseHTML: (el: HTMLElement) => el.getAttribute('frameborder') || '0',
                renderHTML: (attrs: any) => ({ frameborder: attrs.frameborder || '0' }),
            },
            allowfullscreen: {
                default: 'true',
                parseHTML: () => 'true',
                renderHTML: () => ({ allowfullscreen: 'true' }),
            },
            allow: {
                default:
                    'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
                parseHTML: (el: HTMLElement) =>
                    el.getAttribute('allow') ||
                    'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
                renderHTML: (attrs: any) => ({ allow: attrs.allow }),
            },
        };
    },
    parseHTML() {
        return [{ tag: 'iframe' }];
    },
    renderHTML({ HTMLAttributes }) {
        return [
            'iframe',
            mergeAttributes(HTMLAttributes, { class: 'w-full aspect-video rounded-lg my-2' }),
        ];
    },
});

function toYouTubeEmbed(url: string): string | null {
    try {
        if (url.includes('youtube.com/watch')) {
            const v = new URLSearchParams(new URL(url).search).get('v');
            return v ? `https://www.youtube.com/embed/${v}?rel=0` : null;
        }
        if (url.includes('youtu.be/')) {
            const id = url.split('youtu.be/')[1]?.split('?')[0];
            return id ? `https://www.youtube.com/embed/${id}?rel=0` : null;
        }
    } catch (_) {}
    return null;
}

interface LinkModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (url: string) => void;
    initialUrl?: string;
}

const LinkModal: React.FC<LinkModalProps> = ({ isOpen, onClose, onConfirm, initialUrl = '' }) => {
    const [url, setUrl] = useState(initialUrl);

    useEffect(() => {
        if (isOpen) setUrl(initialUrl || '');
    }, [isOpen, initialUrl]);

    if (!isOpen) return null;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onConfirm(url);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
            <div
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6 relative animate-fade-in"
                onClick={e => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                    <X className="w-5 h-5" />
                </button>

                <div className="flex items-start space-x-4">
                    <div className="p-3 rounded-full bg-primary/10 text-primary flex-shrink-0">
                        <LinkIcon className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                            Insert Link
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                            Enter the destination URL for this link.
                        </p>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">URL</label>
                                <input
                                    autoFocus
                                    type="text"
                                    value={url}
                                    onChange={(e) => setUrl(e.target.value)}
                                    placeholder="https://example.com"
                                    className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none text-gray-900 dark:text-white transition-all shadow-sm"
                                />
                            </div>

                            <div className="flex space-x-3 justify-end">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2 text-sm font-medium bg-primary hover:bg-primary-600 text-white rounded-lg transition-all shadow-sm shadow-primary/20"
                                >
                                    Insert Link
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

interface VideoModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Submit a URL — caller decides plain insert vs analyze flow based on `analyze`. */
    onInsertUrl: (url: string, analyze: boolean) => void;
    /** Submit a file — caller decides plain upload vs upload-then-analyze. */
    onUploadFile: (file: File, analyze: boolean) => Promise<void>;
    uploading: boolean;
    /** Plan-VideoAnalysis.md §B.6 — true while a sync analyzeVideo call is in
     *  flight. Modal stays open showing a spinner instead of dismissing on
     *  submit, so the user understands what's happening during the ~30s wait. */
    analyzing: boolean;
    canUpload: boolean;
}

const VideoModal: React.FC<VideoModalProps> = ({
    isOpen,
    onClose,
    onInsertUrl,
    onUploadFile,
    uploading,
    analyzing,
    canUpload,
}) => {
    const [url, setUrl] = useState('');
    // Analyze toggle local to this dialog. Plan §B.4: default OFF.
    const [analyze, setAnalyze] = useState(false);
    const { billing } = useBilling();
    useEffect(() => {
        if (isOpen) {
            setUrl('');
            setAnalyze(false);
        }
    }, [isOpen]);
    if (!isOpen) return null;

    const planKey = (billing?.plan ?? 'trial') as Plan;
    const limit = billing?.usage_limits?.max_video_analyses
        ?? VIDEO_ANALYSIS_DEFAULT_QUOTA[planKey];
    const used = billing?.usage?.video_analyses ?? 0;
    const remaining = limit < 0 ? '∞' : Math.max(0, limit - used);
    const limitDisplay = limit < 0 ? 'unlimited' : limit.toString();
    // Plan-TextVoicePoolBilling-2026-08 §3 — `max_video_analyses` is retired
    // in pooled mode: a video analysis draws text-pool tokens like any other
    // AI surface, so this "3 of 30 remaining" counter would quote a limit
    // that no longer gates anything. Hidden, not deleted (Phase D deletes).
    const pooledBilling = isPooledBilling(billing);

    // §B.1 — analyze is gated to YouTube URLs (non-YouTube text in the input
    // disables the toggle). For uploaded files the size check happens in
    // `onUploadFile` (we don't have the file here, only after the user picks
    // one); the toggle says "Analyze" generically, with the file-specific
    // gate enforced on the file-input change handler — but that requires
    // knowing the file before the click, so we keep the toggle freely
    // tickable for files and let the caller surface size errors via toast.
    const urlIsYoutube = url.trim().length > 0 && isYouTubeUrl(url);
    const togglePillEnabled = urlIsYoutube || canUpload; // enabled if either path is available
    // When the toggle is disabled, distinguish between (a) no upload context —
    // canUpload=false means the parent didn't pass an agentId, which is the
    // KnowledgeHub edit-modal edge case where editingArticle.agent_id wasn't
    // resolved — versus (b) the URL is non-YouTube. Different remedies for
    // the two cases, so different tooltips.
    const tooltipReason = !togglePillEnabled
        ? !canUpload
            ? 'AI analysis is not available — agent context missing. Try reloading the article from the Knowledge Hub list.'
            : 'AI analysis supports YouTube URLs and uploaded files only.'
        : !urlIsYoutube && url.trim().length > 0
            ? 'Paste a YouTube URL to analyze, or use Upload File.'
            : null;

    // While analyzing, render a focused "working" state instead of the form
    // — prevents the user from re-submitting and clarifies the wait window.
    const renderAnalyzingState = () => (
        <div className="text-center py-4 space-y-3">
            <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-white animate-pulse" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Analyzing video with AI
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
                Gemini is generating structured article content. This usually takes about 30 seconds.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-purple-600 dark:text-purple-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Working…</span>
            </div>
        </div>
    );

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={analyzing || uploading ? undefined : onClose}>
            <div
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6 relative"
                onClick={e => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    disabled={analyzing || uploading}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <X className="w-5 h-5" />
                </button>
                {analyzing ? (
                    renderAnalyzingState()
                ) : (
                    <div className="flex items-start space-x-4">
                        <div className="p-3 rounded-full bg-primary/10 text-primary flex-shrink-0">
                            <VideoIcon className="w-6 h-6" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Insert Video</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                                Paste a YouTube URL, a direct video URL, or upload a file.
                            </p>
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    if (!url.trim()) return;
                                    // Analyze gate (§B.1): only fire analyze flow if the URL is YouTube.
                                    const wantAnalyze = analyze && urlIsYoutube;
                                    onInsertUrl(url.trim(), wantAnalyze);
                                    if (!wantAnalyze) onClose();
                                }}
                                className="space-y-3"
                            >
                                <input
                                    autoFocus
                                    type="text"
                                    value={url}
                                    onChange={(e) => setUrl(e.target.value)}
                                    placeholder="https://youtube.com/watch?v=… or https://…/video.mp4"
                                    className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none text-gray-900 dark:text-white"
                                />

                                {/* Plan-VideoAnalysis.md §C.2 — analyze toggle inside the
                                    Insert Video dialog. Same B.1/B.2/B.5 gates as the
                                    upload + URL paste flows in KnowledgeHub.tsx. */}
                                <div className="flex items-start justify-between gap-3 px-3 py-2.5 rounded-lg bg-purple-50/40 dark:bg-purple-900/10 border border-purple-200/50 dark:border-purple-800/40">
                                    <div className="min-w-0">
                                        <label className={`flex items-center gap-1.5 text-xs font-medium ${
                                            togglePillEnabled
                                                ? 'text-gray-700 dark:text-gray-300'
                                                : 'text-gray-400 dark:text-gray-500'
                                        }`}>
                                            <VideoIcon className="w-3.5 h-3.5 text-purple-500 dark:text-purple-400" />
                                            Analyze video to generate article content
                                        </label>
                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                            {tooltipReason ? (
                                                <span className="flex items-start gap-1 text-amber-600 dark:text-amber-400">
                                                    <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                                    {tooltipReason}
                                                </span>
                                            ) : pooledBilling ? (
                                                <>Uses your AI text allowance.</>
                                            ) : (
                                                <>
                                                    Uses 1 of {limitDisplay} monthly video analyses
                                                    {limit > 0 && ` · ${remaining} remaining`}.
                                                </>
                                            )}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        disabled={!togglePillEnabled || uploading}
                                        title={tooltipReason ?? undefined}
                                        onClick={() => setAnalyze(v => !v)}
                                        className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                            !togglePillEnabled
                                                ? 'bg-gray-200 dark:bg-gray-700 cursor-not-allowed opacity-60'
                                                : analyze
                                                    ? 'bg-purple-500 cursor-pointer'
                                                    : 'bg-gray-300 dark:bg-gray-600 cursor-pointer'
                                        }`}
                                    >
                                        <span
                                            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                                analyze && togglePillEnabled ? 'translate-x-4' : 'translate-x-0'
                                            }`}
                                        />
                                    </button>
                                </div>

                                <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                                    {canUpload && (
                                        <label className="px-4 py-2 text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center justify-center gap-2">
                                            {uploading && <Loader2 className="w-4 h-4 animate-spin" />}
                                            <span>{uploading ? 'Uploading…' : 'Upload File'}</span>
                                            <input
                                                type="file"
                                                accept="video/*"
                                                className="hidden"
                                                disabled={uploading}
                                                onChange={async (e) => {
                                                    const f = e.target.files?.[0];
                                                    if (!f) return;
                                                    // §B.2 file-size guard. If the user
                                                    // toggled "analyze" on AND the file
                                                    // exceeds the plan-tier cap, fall back
                                                    // to plain upload + surface a toast
                                                    // via the parent's onUploadFile error
                                                    // path (formatAnalyzeError handles 413).
                                                    const planCap = VIDEO_ANALYSIS_PLAN_CAP_MB[planKey];
                                                    const fileMb = f.size / (1024 * 1024);
                                                    const tooLarge = fileMb > planCap;
                                                    const wantAnalyze = analyze && isVideoFile(f) && !tooLarge;
                                                    await onUploadFile(f, wantAnalyze);
                                                    // Modal is dismissed by parent on success.
                                                }}
                                            />
                                        </label>
                                    )}
                                    <button
                                        type="submit"
                                        disabled={!url.trim() || uploading}
                                        className="px-6 py-2 text-sm font-medium bg-primary hover:bg-primary-600 text-white rounded-lg transition-all shadow-sm shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Insert URL
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

interface MenuBarProps {
    editor: any;
    mode: RichTextEditorMode;
    agentId?: string;
    onMediaUploaded?: (s3Key: string) => void;
}

// Contract-KnowledgeTablePaste.md §3 "UI constraints": ONE toolbar entry for
// every table control, not eight buttons — the toolbar is already crowded at
// 375px. 🚨 Same shape as `admin-front`'s TableMenu; the only intentional
// difference is the ConfirmModal prop API, which differs between the repos
// (`isOpen`/`confirmText`/`isDangerous` here, `open`/`confirmLabel`/`tone` there).
//
// The panel is `absolute` against the TOOLBAR (which is `relative`), not
// against this button. The toolbar wraps at 375px, so the button's own x
// position is unpredictable; anchoring to the toolbar keeps the panel inside
// the editor's width wherever the button happens to land.
const TableMenu: React.FC<{ editor: any }> = ({ editor }) => {
    const [open, setOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open]);

    const inTable = editor?.isActive('table') ?? false;

    const run = (fn: () => void) => {
        fn();
        setOpen(false);
    };

    const itemBase =
        'w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed';
    const itemNormal =
        'text-gray-700 dark:text-gray-200 enabled:hover:bg-gray-100 dark:enabled:hover:bg-gray-700';
    const itemDanger =
        'text-red-600 dark:text-red-300 enabled:hover:bg-red-50 dark:enabled:hover:bg-red-900/30';

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={open}
                className={`p-1.5 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
                    inTable ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white' : ''
                } inline-flex items-center gap-0.5`}
                title="Table"
            >
                <TableIcon className="w-4 h-4" />
                <ChevronDown className="w-3 h-3 opacity-70" />
            </button>

            {open && (
                <>
                    <button
                        type="button"
                        aria-label="Close table menu"
                        className="fixed inset-0 z-40 cursor-default"
                        onClick={() => setOpen(false)}
                    />
                    <div
                        role="menu"
                        className="absolute left-2 top-full mt-1 z-50 w-56 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl p-1"
                    >
                        <button
                            type="button"
                            role="menuitem"
                            className={`${itemBase} ${itemNormal}`}
                            onClick={() =>
                                run(() =>
                                    editor
                                        .chain()
                                        .focus()
                                        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                                        .run(),
                                )
                            }
                        >
                            <TableIcon className="w-3.5 h-3.5" />
                            Insert table
                        </button>

                        <div className="my-1 h-px bg-gray-200 dark:bg-gray-700" />
                        {/* Everything below only means anything inside a table. */}
                        <button type="button" role="menuitem" disabled={!inTable} className={`${itemBase} ${itemNormal}`}
                            onClick={() => run(() => editor.chain().focus().addRowBefore().run())}>
                            <ArrowUp className="w-3.5 h-3.5" /> Insert row above
                        </button>
                        <button type="button" role="menuitem" disabled={!inTable} className={`${itemBase} ${itemNormal}`}
                            onClick={() => run(() => editor.chain().focus().addRowAfter().run())}>
                            <ArrowDown className="w-3.5 h-3.5" /> Insert row below
                        </button>
                        <button type="button" role="menuitem" disabled={!inTable} className={`${itemBase} ${itemNormal}`}
                            onClick={() => run(() => editor.chain().focus().addColumnBefore().run())}>
                            <ArrowLeft className="w-3.5 h-3.5" /> Insert column left
                        </button>
                        <button type="button" role="menuitem" disabled={!inTable} className={`${itemBase} ${itemNormal}`}
                            onClick={() => run(() => editor.chain().focus().addColumnAfter().run())}>
                            <ArrowRight className="w-3.5 h-3.5" /> Insert column right
                        </button>

                        <div className="my-1 h-px bg-gray-200 dark:bg-gray-700" />
                        {/* 🚨 Spreadsheet and Google Docs pastes arrive as ALL <td> —
                            those apps don't emit <th>. This toggle is how an author
                            gets the HC's styled header row back, so it is not a nicety. */}
                        <button type="button" role="menuitem" disabled={!inTable} className={`${itemBase} ${itemNormal}`}
                            onClick={() => run(() => editor.chain().focus().toggleHeaderRow().run())}>
                            <PanelTop className="w-3.5 h-3.5" /> Toggle header row
                        </button>

                        <div className="my-1 h-px bg-gray-200 dark:bg-gray-700" />
                        <button type="button" role="menuitem" disabled={!inTable} className={`${itemBase} ${itemDanger}`}
                            onClick={() => run(() => editor.chain().focus().deleteRow().run())}>
                            <Trash2 className="w-3.5 h-3.5" /> Delete row
                        </button>
                        <button type="button" role="menuitem" disabled={!inTable} className={`${itemBase} ${itemDanger}`}
                            onClick={() => run(() => editor.chain().focus().deleteColumn().run())}>
                            <Trash2 className="w-3.5 h-3.5" /> Delete column
                        </button>
                        <button type="button" role="menuitem" disabled={!inTable} className={`${itemBase} ${itemDanger}`}
                            onClick={() => {
                                setOpen(false);
                                setConfirmDelete(true);
                            }}>
                            <Trash2 className="w-3.5 h-3.5" /> Delete table
                        </button>
                    </div>
                </>
            )}

            {/* 🚨 ConfirmModal, never window.confirm. */}
            <ConfirmModal
                isOpen={confirmDelete}
                isDangerous
                title="Delete table"
                confirmText="Delete"
                message="Delete this table and everything in it? This cannot be undone."
                onConfirm={() => {
                    editor.chain().focus().deleteTable().run();
                    setConfirmDelete(false);
                }}
                onClose={() => setConfirmDelete(false)}
            />
        </>
    );
};

const MenuBar: React.FC<MenuBarProps> = ({ editor, mode, agentId, onMediaUploaded }) => {
    const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
    const [videoUploading, setVideoUploading] = useState(false);
    // Plan-VideoAnalysis.md §B.6 — sync HTTP analyze; while in flight the
    // VideoModal renders a "Working…" state instead of the form.
    const [videoAnalyzing, setVideoAnalyzing] = useState(false);
    const { showToast } = useToast();
    const { billing } = useBilling();

    if (!editor) {
        return null;
    }

    const addImage = useCallback(async () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'image/*';
        fileInput.onchange = async (e: any) => {
            if (e.target.files && e.target.files[0]) {
                const file = e.target.files[0];
                try {
                    if (mode === 'article' && agentId) {
                        const res = await knowledgeAPI.uploadImage(agentId, file);
                        const key = extractS3Key(res.data.url);
                        if (key) onMediaUploaded?.(key);
                        editor.chain().focus().setImage({ src: res.data.url, alt: res.data.filename }).run();
                    } else {
                        const reader = new FileReader();
                        reader.onload = (readerEvent) => {
                            const url = readerEvent.target?.result as string;
                            editor.chain().focus().setImage({ src: url }).run();
                        };
                        reader.readAsDataURL(file);
                    }
                } catch (error) {
                    console.error('Failed to upload image', error);
                }
            }
        };
        fileInput.click();
    }, [editor, mode, agentId, onMediaUploaded]);

    const handleSetLink = (url: string) => {
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    };

    /** Fallback plain insertion — used when the analyze toggle is OFF or when
     *  analyze fails. Preserves the original behavior so the editor flow
     *  never hangs because of analysis errors. */
    const insertPlainVideoUrl = (url: string) => {
        const embedUrl = toYouTubeEmbed(url);
        if (embedUrl) {
            editor
                .chain()
                .focus()
                .insertContent(
                    `<iframe src="${embedUrl}" frameborder="0" allowfullscreen="true" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"></iframe><p></p>`
                )
                .run();
        } else {
            editor
                .chain()
                .focus()
                .insertContent(
                    `<video src="${url}" controls preload="metadata"></video><p></p>`
                )
                .run();
        }
    };

    /** Insert from URL — plain mode if `analyze=false`; otherwise call
     *  `knowledgeAIAPI.analyzeVideo` and inject the full Plan §B.7 layout
     *  (video iframe + hr-trio + Gemini HTML + hr-trio). */
    const insertVideoUrl = async (url: string, analyze: boolean) => {
        if (!analyze || !agentId) {
            insertPlainVideoUrl(url);
            return;
        }
        setVideoAnalyzing(true);
        try {
            const res = await knowledgeAIAPI.analyzeVideo({
                video_url: url,
                agent_id: agentId,
                format: 'structured_html',
            });
            const block = youtubeEmbedHtml(url);
            editor
                .chain()
                .focus()
                .insertContent(buildAnalyzedArticleHtml(block, res.data.content_html) + '<p></p>')
                .run();
            setIsVideoModalOpen(false);
        } catch (err) {
            // Plan §C.2 error UX — never block the underlying insertion. Drop
            // back to a plain video insert so the user keeps their video, and
            // surface a friendly toast about the analysis failure.
            console.error('Analyze video URL failed', err);
            showToast(formatAnalyzeError(err, billing?.plan), 'error');
            insertPlainVideoUrl(url);
            setIsVideoModalOpen(false);
        } finally {
            setVideoAnalyzing(false);
        }
    };

    const handleUploadVideo = async (file: File, analyze: boolean) => {
        if (!agentId) return;
        setVideoUploading(true);
        try {
            const res = await knowledgeAPI.uploadVideo(agentId, file);
            const key = extractS3Key(res.data.url);
            if (key) onMediaUploaded?.(key);
            // Plain insertion path — same as before the analyze feature.
            if (!analyze || !key) {
                editor
                    .chain()
                    .focus()
                    .insertContent(
                        `<video src="${res.data.url}" controls preload="metadata"></video><p></p>`
                    )
                    .run();
                setIsVideoModalOpen(false);
                if (analyze && !key) {
                    // Could not parse an S3 key off the upload response — uploaded
                    // file is in S3 but we can't ask backend to analyze without
                    // a key. Surface a toast so the operator knows why analyze
                    // didn't fire (tomorrow we add `s3_key` direct to the upload
                    // response and skip this fallback).
                    showToast(
                        'Uploaded but couldn\'t locate the file in S3 for analysis. Try the URL flow or re-upload.',
                        'info',
                    );
                }
                return;
            }
            // Analyze path — file is in S3, fire `/analyze-video` with the key.
            setVideoUploading(false);
            setVideoAnalyzing(true);
            try {
                const analyzeRes = await knowledgeAIAPI.analyzeVideo({
                    video_s3_key: key,
                    agent_id: agentId,
                    format: 'structured_html',
                });
                const block = uploadedVideoEmbedHtml(res.data.url);
                editor
                    .chain()
                    .focus()
                    .insertContent(
                        buildAnalyzedArticleHtml(block, analyzeRes.data.content_html) + '<p></p>'
                    )
                    .run();
                setIsVideoModalOpen(false);
            } catch (err) {
                console.error('Analyze uploaded video failed', err);
                showToast(formatAnalyzeError(err, billing?.plan), 'error');
                // Fall back to plain insert so the user still keeps their video.
                editor
                    .chain()
                    .focus()
                    .insertContent(
                        `<video src="${res.data.url}" controls preload="metadata"></video><p></p>`
                    )
                    .run();
                setIsVideoModalOpen(false);
            } finally {
                setVideoAnalyzing(false);
            }
            return;
        } catch (error) {
            console.error('Failed to upload video', error);
            showToast('Failed to upload video.', 'error');
        } finally {
            setVideoUploading(false);
        }
    };

    const btnBase = 'p-1.5 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors';
    const btnActive = 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white';

    return (
        // `relative` is the positioning context for TableMenu's panel — see the
        // comment on that component for why it anchors here and not to its button.
        <div className="relative flex flex-wrap items-center gap-1 p-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 rounded-t-xl">
            {mode === 'article' && (
                <>
                    <button
                        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                        className={`${btnBase} ${editor.isActive('heading', { level: 1 }) ? btnActive : ''}`}
                        title="Heading 1"
                    >
                        <Heading1 className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                        className={`${btnBase} ${editor.isActive('heading', { level: 2 }) ? btnActive : ''}`}
                        title="Heading 2"
                    >
                        <Heading2 className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                        className={`${btnBase} ${editor.isActive('heading', { level: 3 }) ? btnActive : ''}`}
                        title="Heading 3"
                    >
                        <Heading3 className="w-4 h-4" />
                    </button>
                    <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1" />
                </>
            )}

            <button
                type="button"
                onClick={() => editor.chain().focus().toggleBold().run()}
                disabled={!editor.can().chain().focus().toggleBold().run()}
                className={`${btnBase} ${editor.isActive('bold') ? btnActive : ''}`}
                title="Bold"
            >
                <Bold className="w-4 h-4" />
            </button>
            <button
                type="button"
                onClick={() => editor.chain().focus().toggleItalic().run()}
                disabled={!editor.can().chain().focus().toggleItalic().run()}
                className={`${btnBase} ${editor.isActive('italic') ? btnActive : ''}`}
                title="Italic"
            >
                <Italic className="w-4 h-4" />
            </button>

            <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1" />

            <button
                type="button"
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                className={`${btnBase} ${editor.isActive('bulletList') ? btnActive : ''}`}
                title="Bullet List"
            >
                <List className="w-4 h-4" />
            </button>
            <button
                type="button"
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                className={`${btnBase} ${editor.isActive('orderedList') ? btnActive : ''}`}
                title="Ordered List"
            >
                <ListOrdered className="w-4 h-4" />
            </button>
            <button
                type="button"
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                className={`${btnBase} ${editor.isActive('blockquote') ? btnActive : ''}`}
                title="Blockquote"
            >
                <Quote className="w-4 h-4" />
            </button>

            <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1" />

            <button
                type="button"
                onClick={() => setIsLinkModalOpen(true)}
                className={`${btnBase} ${editor.isActive('link') ? btnActive : ''}`}
                title="Insert Link"
            >
                <LinkIcon className="w-4 h-4" />
            </button>
            <button type="button" onClick={addImage} className={btnBase} title="Insert Image">
                <ImageIcon className="w-4 h-4" />
            </button>

            {mode === 'article' && (
                <>
                    <button
                        onClick={() => setIsVideoModalOpen(true)}
                        className={btnBase}
                        title="Insert Video"
                    >
                        <VideoIcon className="w-4 h-4" />
                    </button>
                    <TableMenu editor={editor} />
                    <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1" />
                    <button
                        onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
                        className={btnBase}
                        title="Clear formatting"
                    >
                        <RemoveFormatting className="w-4 h-4" />
                    </button>
                </>
            )}

            <LinkModal
                isOpen={isLinkModalOpen}
                onClose={() => setIsLinkModalOpen(false)}
                onConfirm={handleSetLink}
                initialUrl={editor.getAttributes('link').href}
            />
            <VideoModal
                isOpen={isVideoModalOpen}
                onClose={() => setIsVideoModalOpen(false)}
                onInsertUrl={insertVideoUrl}
                onUploadFile={handleUploadVideo}
                uploading={videoUploading}
                analyzing={videoAnalyzing}
                canUpload={!!agentId}
            />
        </div>
    );
};

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
    content,
    onChange,
    placeholder = 'Write a reply...',
    mode = 'email',
    agentId,
    onMediaUploaded,
}) => {
    // Refs keep handlers up-to-date without re-initializing the editor
    const agentIdRef = useRef(agentId);
    agentIdRef.current = agentId;
    const onMediaUploadedRef = useRef(onMediaUploaded);
    onMediaUploadedRef.current = onMediaUploaded;
    const modeRef = useRef(mode);
    modeRef.current = mode;

    // allowBase64 is REQUIRED so that TipTap's parseHTML rule accepts
    // <img src="data:image/..."> on setContent. Default is false, which means
    // the schema drops every data-URL image silently — breaking articles
    // ingested from .docx / .pdf that embed images as base64.
    // (ResizableAlignableImage flips the same default in its own addOptions().)
    const extensions = [
        StarterKit,
        mode === 'article'
            ? ResizableAlignableImage
            : Image.configure({ allowBase64: true }),
        Link.configure({
            openOnClick: false,
        }),
        Placeholder.configure({
            placeholder,
        }),
    ];
    if (mode === 'article') {
        // 🚨 §2 invariant: this list, these options, THIS ORDER must match
        // `admin-front/src/components/knowledge/RichTextEditor.tsx` exactly.
        // Both editors feed one `article.html` and there is no per-source
        // branch to absorb a difference.
        //
        // Scoped to article mode (the Knowledge Hub path) on purpose — the
        // compact mode is the Inbox email composer, which is out of scope and
        // should keep flattening pasted tables as it does today.
        //
        // `resizable` is left at its default of false: the HC has no styling
        // for resize handles, and per the probe it makes NO difference to
        // whether a <colgroup> is emitted — TipTap emits one either way.
        extensions.push(VideoBlock as any, IframeBlock as any, TableKit as any);
    }

    const editor = useEditor({
        extensions,
        content,
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML(), editor.getText());
        },
        editorProps: {
            attributes: {
                class:
                    mode === 'article'
                        ? `prose dark:prose-invert prose-headings:font-bold prose-p:my-2 prose-headings:mt-4 prose-headings:mb-2 max-w-none focus:outline-none min-h-[320px] max-h-[60vh] overflow-y-auto px-4 py-3 ${TABLE_PROSE_CLASSES}`
                        : 'prose prose-sm dark:prose-invert prose-p:my-1 prose-headings:my-2 focus:outline-none min-h-[120px] max-h-[300px] overflow-y-auto px-4 py-3',
            },
            // Owner decision: first row of a PASTED table becomes a header row.
            // Slice-scoped, so existing document content is never affected.
            transformPasted: (slice, view) =>
                promoteFirstRowToHeader(slice, view.state.schema),
            handlePaste: (view, event) => {
                const items = event.clipboardData?.items;
                if (!items) return false;
                for (const item of Array.from(items)) {
                    if (item.type.startsWith('image/')) {
                        const file = item.getAsFile();
                        if (!file) continue;
                        const curAgentId = agentIdRef.current;
                        const curMode = modeRef.current;
                        if (curMode === 'article' && curAgentId) {
                            event.preventDefault();
                            knowledgeAPI
                                .uploadImage(curAgentId, file)
                                .then((res) => {
                                    const url = res.data.url;
                                    const key = extractS3Key(url);
                                    if (key) onMediaUploadedRef.current?.(key);
                                    const { schema } = view.state;
                                    const imageNodeType =
                                        schema.nodes.imageResize || schema.nodes.image;
                                    if (!imageNodeType) return;
                                    const imageNode = imageNodeType.create({ src: url });
                                    const tr = view.state.tr.replaceSelectionWith(imageNode);
                                    view.dispatch(tr);
                                })
                                .catch((err) => console.error('Failed to paste image', err));
                            return true;
                        }
                        // Email mode fallback: embed as base64 data URL
                        if (curMode === 'email') {
                            event.preventDefault();
                            const reader = new FileReader();
                            reader.onload = (readerEvent) => {
                                const url = readerEvent.target?.result as string;
                                const { schema } = view.state;
                                const imageNode = schema.nodes.image?.create({ src: url });
                                if (!imageNode) return;
                                const tr = view.state.tr.replaceSelectionWith(imageNode);
                                view.dispatch(tr);
                            };
                            reader.readAsDataURL(file);
                            return true;
                        }
                    }
                }
                return false;
            },
        },
    });

    // Sync external content changes into the editor (e.g. AI-generated content injected from parent)
    useEffect(() => {
        if (!editor) return;
        const currentHtml = editor.getHTML();
        if (content !== currentHtml) {
            editor.commands.setContent(content, { emitUpdate: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content]);

    return (
        <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 flex flex-col focus-within:ring-2 focus-within:ring-primary/50 focus-within:border-primary transition-all">
            <MenuBar editor={editor} mode={mode} agentId={agentId} onMediaUploaded={onMediaUploaded} />
            <EditorContent editor={editor} />
        </div>
    );
};

export default RichTextEditor;
